<template>
    <div class="container">
        <div class="appeal">
            <a-button type="text" class="appeal_back" @click="goBack">
                <template #icon>
                    <arrow-left-outlined />
                </template>
            </a-button>

            <h1 class="appeal_title">
                Страховое дело по выплате {{ paymentNumber }}
            </h1>

            <!-- Статус ведения выплаты приходит из карточки выплаты: эндпоинт документов
                 его не отдаёт, поэтому без него шкалу не рисуем. -->
            <div v-if="currentStep !== -1" class="steps">
                <div v-for="(step, index) in statusSteps" :key="step.code" class="steps_item">
                    <div class="steps_head">
                        <div v-if="index" class="steps_line" :class="{ steps_line_done: index <= currentStep }"></div>
                        <div class="steps_circle" :class="{
                            steps_circle_done: index < currentStep,
                            steps_circle_current: index === currentStep
                        }">
                            <span class="steps_number">{{ String(index + 1).padStart(2, '0') }}</span>
                        </div>
                    </div>
                    <p class="steps_label">{{ step.label }}</p>
                </div>
            </div>

            <a-alert v-if="requiredCount" type="warning" show-icon class="appeal_alert"
                message="От вас требуются документы" :description="requiredDescription">
                <template #icon>
                    <bell-filled />
                </template>
            </a-alert>

            <div class="docs_header">
                <h2 class="docs_title">Перечень документов</h2>
                <div v-if="totalCount" class="docs_progress">
                    <div class="docs_progress_track">
                        <div class="docs_progress_bar" :style="{ width: `${progressPercent}%` }"></div>
                    </div>
                    <span class="docs_progress_badge" :style="{ left: `${progressPercent}%` }">
                        {{ providedCount }}/{{ totalCount }}
                    </span>
                </div>
            </div>

            <div v-if="totalCount" class="docs_toolbar">
                <div class="docs_filters">
                    <button v-for="filter in filters" :key="filter.key" class="docs_filter"
                        :class="{ docs_filter_active: filter.key === activeFilter }"
                        @click="activeFilter = filter.key">
                        {{ filter.label }} <span>({{ countByFilter(filter.key) }})</span>
                    </button>
                </div>
            </div>

            <a-spin :spinning="isLoading">
                <p v-if="!isLoading && !filteredDocuments.length" class="docs_empty">
                    {{ totalCount ? 'В этой группе документов нет' : 'По выплате пока нет документов' }}
                </p>

                <a-list v-else :data-source="filteredDocuments" :split="false" class="docs_list">
                    <template #renderItem="{ item }">
                        <a-list-item class="doc">
                            <a-list-item-meta :title="item.name">
                                <template #avatar>
                                    <file-outlined class="doc_icon" />
                                </template>
                                <template #description>
                                    <div class="doc_meta">
                                        <span class="doc_status" :class="`doc_status_${item.status}`">
                                            {{ item.status_label }}
                                        </span>
                                        <span v-if="item.date" class="doc_date">от {{ item.date }}</span>
                                    </div>

                                    <!-- Файлы, принятые КИАС: скачиваются через getPrintableDocument
                                         по индексу (download_url), путь наружу не отдаётся. -->
                                    <p v-if="item.attachments?.length" class="doc_attachments">
                                        Принято страховщиком:
                                        <button v-for="attachment in item.attachments" :key="attachment.download_url"
                                            type="button" class="doc_attachment"
                                            @click="downloadAttachment(attachment)">{{ attachment.name }}</button>
                                    </p>

                                    <button v-if="item.file" type="button" class="doc_own_file"
                                        @click="downloadOwnFile(item)">
                                        <download-outlined /> {{ item.file.name }}
                                        <span v-if="item.file.size">({{ formatFileSize(item.file.size) }})</span>
                                    </button>
                                </template>
                            </a-list-item-meta>
                            <template #extra>
                                <a-button v-if="item.can_upload" type="primary" class="doc_upload"
                                    @click="openUpload(item)">
                                    <template #icon>
                                        <upload-outlined />
                                    </template>
                                    Загрузить
                                </a-button>

                                <!-- can_upload всегда false для заявлений: они заполняются
                                     формой и подписываются, а не прикрепляются файлом. -->
                                <a-button v-else-if="item.can_fill_form" type="primary" class="doc_upload"
                                    :loading="openingForm === item.doc_isn" @click="openForm(item)">
                                    <template #icon>
                                        <form-outlined />
                                    </template>
                                    Заполнить и подписать
                                </a-button>
                            </template>
                        </a-list-item>
                    </template>
                </a-list>
            </a-spin>
        </div>

        <DocumentModal :open="isModalOpen" :type="modalType" :document="uploadingDocument" :loading="isUploading"
            :company-id="companyId" :prefill="formPrefill" @submit="onModalSubmit" @abandon="abandonSigning"
            @close="isModalOpen = false" />
    </div>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { Alert, Button, List, Spin } from 'ant-design-vue';
import {
    ArrowLeftOutlined,
    BellFilled,
    DownloadOutlined,
    FileOutlined,
    FormOutlined,
    UploadOutlined,
} from '@ant-design/icons-vue';
import axios from 'axios';
import { useToast } from 'vue-toastification';
import store from '@/store';
import DocumentModal from './components/document-modal.vue';

const AAlert = Alert;
const AButton = Button;
const AList = List;
const AListItem = List.Item;
const AListItemMeta = List.Item.Meta;
const ASpin = Spin;

const route = useRoute();
const router = useRouter();
const toaster = useToast();

// :id — id выплаты из /V1/my-insurance-payments, не заявки.
const paymentId = computed(() => route.params.id);

// Шапку (номер договора, статус ведения) тянем отдельным запросом по id выплаты,
// а не из query-параметров списка: при прямом заходе/обновлении их нет, а метка
// статуса из query легко расходится со справочником. Query — запасной вариант.
const payment = ref(null);
const paymentNumber = computed(
    () => payment.value?.contract?.number || route.query.number || route.params.id
);

// company_id обязателен во всех трёх эндпоинтах, иначе 404. Карточка выплаты
// кладёт его в query; при прямом заходе берём первую компанию пользователя.
const companyId = computed(() => {
    const fromQuery = Number(route.query.company_id);
    if (fromQuery) return fromQuery;
    return store.getters['auth/getUser']?.companies?.[0]?.id ?? null;
});

const statusSteps = [
    { code: 'no_documents', label: 'Нет документов' },
    { code: 'no_driver_documents', label: 'Нет документов водителя' },
    { code: 'no_sto_invoice', label: 'Нет счёта СТО' },
    { code: 'sto_invoice_approval', label: 'Согласование счёта СТО' },
    { code: 'no_payment_request', label: 'Нет заявления для выплаты' },
    { code: 'full_package', label: 'Полный пакет документов' },
    { code: 'settled', label: 'Урегулировано' },
];

// Статус ведения приходит из карточки выплаты меткой, а не кодом. Сравниваем
// нормализованно: витринные подписи шагов и значения enum расходятся регистром
// и ё/е («Нет счёта СТО» vs «Нет счета сто») — точное сравнение прятало шкалу.
const normalizeTracking = (value) => String(value ?? '').toLowerCase().replace(/ё/g, 'е').trim();
const trackingStatus = computed(() => payment.value?.payment_tracking_status ?? route.query.tracking);
const currentStep = computed(() =>
    statusSteps.findIndex(step => normalizeTracking(step.label) === normalizeTracking(trackingStatus.value))
);

const documents = ref([]);
const isLoading = ref(false);

const STATUS_ORDER = { required: 0, pending_review: 1, provided: 2 };

const sortDocuments = (list) => [...list].sort((a, b) => {
    const byStatus = (STATUS_ORDER[a.status] ?? 99) - (STATUS_ORDER[b.status] ?? 99);
    return byStatus || String(a.name).localeCompare(String(b.name), 'ru');
});

const errorMessage = (error, fallback) => {
    if (axios.isAxiosError(error) && error.response) {
        return error.response.data?.message || fallback;
    }
    return fallback;
};

// Карточка выплаты по id — источник номера и статуса ведения. Ошибку не
// показываем: шапка деградирует до query-параметров, документы грузятся отдельно.
const getPayment = async () => {
    if (!companyId.value) return;
    try {
        const res = await axios.get(`/V1/my-insurance-payments/${paymentId.value}`, {
            params: { company_id: companyId.value },
        });
        payment.value = res.data.data.payment;
    } catch {
        payment.value = null;
    }
};

const getDocuments = async () => {
    if (!companyId.value) {
        toaster.error('Не удалось определить компанию', { position: 'top-right' });
        return;
    }
    isLoading.value = true;
    try {
        const res = await axios.get(`/V1/my-insurance-payments/${paymentId.value}/documents`, {
            params: { company_id: companyId.value },
        });
        documents.value = sortDocuments(res.data.data.documents || []);
    } catch (error) {
        toaster.error(errorMessage(error, 'Ошибка загрузки документов'), { position: 'top-right' });
    } finally {
        isLoading.value = false;
    }
};

const totalCount = computed(() => documents.value.length);
const providedCount = computed(() => documents.value.filter(item => item.status === 'provided').length);
const requiredCount = computed(() => documents.value.filter(item => item.status === 'required').length);

const requiredDescription = computed(() =>
    `Для продолжения рассмотрения выплаты необходимо предоставить документов: ${requiredCount.value}. `
    + 'После проверки страховщиком статусы будут установлены автоматически'
);

const activeFilter = ref('all');

const filters = [
    { key: 'all', label: 'Все' },
    { key: 'required', label: 'Требуются' },
    { key: 'pending_review', label: 'На проверке' },
    { key: 'provided', label: 'Предоставлены' },
];

const countByFilter = (key) => {
    if (key === 'all') return documents.value.length;
    return documents.value.filter(document => document.status === key).length;
};

const filteredDocuments = computed(() => {
    if (activeFilter.value === 'all') return documents.value;
    return documents.value.filter(document => document.status === activeFilter.value);
});

const progressPercent = computed(() => {
    if (!totalCount.value) return 0;
    return Math.round((providedCount.value / totalCount.value) * 100);
});

const formatFileSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

// Файл, принятый КИАС: тот же blob-паттерн, download_url уже со company_id
// и индексом вложения; путь к файлу резолвится на бэке.
const downloadAttachment = async (attachment) => {
    try {
        const res = await axios.get(attachment.download_url, { responseType: 'blob' });
        const blob = new Blob([res.data]);
        const url = window.URL.createObjectURL(blob);
        const link = window.document.createElement('a');
        link.href = url;
        link.download = attachment.name || 'document';
        window.document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
    } catch (error) {
        toaster.error(errorMessage(error, 'Ошибка скачивания файла'), { position: 'top-right' });
    }
};

// download_url уже содержит company_id; тянем с Bearer-заголовком в blob —
// прямое открытие ссылки браузером даст редирект на логин.
const downloadOwnFile = async (document) => {
    try {
        const res = await axios.get(document.file.download_url, { responseType: 'blob' });
        const blob = new Blob([res.data], { type: document.file.mime_type || 'application/octet-stream' });
        const url = window.URL.createObjectURL(blob);
        const link = window.document.createElement('a');
        link.href = url;
        link.download = document.file.name || 'document';
        window.document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
    } catch (error) {
        toaster.error(errorMessage(error, 'Ошибка скачивания файла'), { position: 'top-right' });
    }
};

const isModalOpen = ref(false);
const isUploading = ref(false);
const modalType = ref('upload');
const uploadingDocument = ref(null);

const openUpload = (document) => {
    uploadingDocument.value = document;
    modalType.value = 'upload';
    isModalOpen.value = true;
};

const formPrefill = ref(null);
const openingForm = ref(null);

// Открытие формы: сначала префилл (страна, ТС, дата события, ссылка на открытую
// сессию), затем модалка — иначе поля мигают пустыми.
const openForm = async (document) => {
    openingForm.value = document.doc_isn;
    try {
        const res = await axios.get(
            `/V1/my-insurance-payments/${paymentId.value}/documents/${document.doc_isn}/form`,
            { params: { company_id: companyId.value } },
        );
        formPrefill.value = res.data.data;
        uploadingDocument.value = { ...document, payment_id: paymentId.value };
        modalType.value = 'form';
        isModalOpen.value = true;
    } catch (error) {
        toaster.error(errorMessage(error, 'Не удалось открыть форму'), { position: 'top-right' });
    } finally {
        openingForm.value = null;
    }
};

// Документ не меняет статус здесь: заявка в КИАС создаётся только после подписания,
// поэтому список обновляем, а строка остаётся «Предоставить документ».
const submitForm = async ({ document, values }) => {
    isUploading.value = true;
    try {
        const res = await axios.post(
            `/V1/my-insurance-payments/${paymentId.value}/documents/${document.doc_isn}/form`,
            values,
            { params: { company_id: companyId.value } },
        );

        isModalOpen.value = false;
        toaster.success('Документ отправлен на подписание', { position: 'top-right' });
        window.open(res.data.data.signature_link, '_blank', 'noopener');
    } catch (error) {
        toaster.error(errorMessage(error, 'Не удалось отправить заявление'), { position: 'top-right' });
    } finally {
        isUploading.value = false;
    }
};

// Клиент не довёл подписание до конца — сбрасываем сессию, чтобы следующая отправка
// выдала новую ссылку, и сразу открываем форму заново.
const abandonSigning = async (document) => {
    isUploading.value = true;
    try {
        await axios.delete(
            `/V1/my-insurance-payments/${paymentId.value}/documents/${document.doc_isn}/form`,
            { params: { company_id: companyId.value } },
        );
        isModalOpen.value = false;
        await openForm(document);
    } catch (error) {
        toaster.error(errorMessage(error, 'Не удалось сбросить подписание'), { position: 'top-right' });
    } finally {
        isUploading.value = false;
    }
};

const onModalSubmit = (payload) => (
    payload.type === 'form' ? submitForm(payload) : uploadDocument(payload)
);

const uploadDocument = async ({ document, values }) => {
    const file = values.files?.[0];
    if (!file || !document) return;

    const formData = new FormData();
    formData.append('file', file);

    isUploading.value = true;
    try {
        // Загрузка идёт по doc_isn — стабильному коду типа документа.
        const res = await axios.post(
            `/V1/my-insurance-payments/${paymentId.value}/documents/${document.doc_isn}`,
            formData,
            { params: { company_id: companyId.value }, headers: { 'Content-Type': 'multipart/form-data' } }
        );

        const updated = res.data.data.document;
        documents.value = sortDocuments(
            documents.value.map(item => (item.doc_isn === document.doc_isn ? updated : item))
        );
        toaster.success(res.data.data.message || 'Документ отправлен на рассмотрение', { position: 'top-right' });
        isModalOpen.value = false;
    } catch (error) {
        toaster.error(errorMessage(error, 'Не удалось загрузить документ'), { position: 'top-right' });
    } finally {
        isUploading.value = false;
    }
};

// router.back() возвращает на /profile/ без раздела, и открывается вкладка
// по умолчанию — поэтому уходим на выплаты явно.
const goBack = () => router.push({ path: '/profile/', query: { section: 'contract_payments' } });

onMounted(() => {
    getPayment();
    getDocuments();
});
</script>

<style lang="scss" scoped>
@mixin fontStyle ($family, $style, $weight, $size, $lheight, $color) {
    font-family: $family;
    font-style: normal;
    font-weight: $weight;
    font-size: $size;
    line-height: $lheight;
    color: $color;
}

.appeal {
    padding: 20px 0 60px;

    &_back {
        display: flex!important;
        justify-content: center;
        align-items: center;
        color: #373737;

        font-size: 22px;
    }

    &_title {
        @include fontStyle(Roboto, normal, 500, 24px, 31px, #373737);
        margin: 10px 0 30px;
    }

    &_alert {
        margin-bottom: 30px;
        border: none;
        border-left: 4px solid #FAAD14;
        border-radius: 0;
    }
}

.steps {
    display: flex;
    align-items: flex-start;
    flex-wrap: nowrap;
    overflow-x: auto;
    gap: 16px;
    margin-bottom: 30px;
    padding-bottom: 4px;

    @media (max-width: 767px) {
        gap: 8px;
    }

    &_item {
        flex: 1 1 0;
        min-width: 110px;
        box-sizing: border-box;
        display: flex;
        flex-direction: column;
        align-items: stretch;
        position: relative;

        @media (max-width: 767px) {
            min-width: 90px;
        }
    }

    // Строка с кружком: относительно неё позиционируется линия,
    // поэтому она попадает точно в центр кружка независимо от подписи.
    &_head {
        width: 100%;
        display: flex;
        justify-content: center;
        position: relative;
    }

    // Линия идёт от центра предыдущего кружка до центра текущего.
    &_line {
        position: absolute;
        top: 50%;
        right: 50%;
        width: 100%;
        height: 2px;
        background: #E8E8E8;
        transform: translateY(-50%);

        &_done {
            background: #52C41A;
        }
    }

    // Диаметр задаёт паддинг вокруг номера, aspect-ratio держит форму круга.
    &_circle {
        aspect-ratio: 1;
        box-sizing: border-box;
        padding: 7px;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        border: 2px solid #D9D9D9;
        border-radius: 50%;
        background: #fff;
        color: #BFBFBF;
        position: relative;
        z-index: 1;

        // Пройденные: зелёный бордер, белый зазор, зелёная заливка.
        // Зазор — inset-тенью, чтобы он не зависел от паддинга, который задаёт размер.
        &_done {
            border-color: #52C41A;
            background: #52C41A;
            box-shadow: inset 0 0 0 2px #fff;
            color: #fff;
        }

        // Текущий: сплошная зелёная заливка без зазора.
        &_current {
            border-color: #52C41A;
            background: #52C41A;
            color: #fff;
        }
    }

    &_number {
        @include fontStyle(Roboto, normal, 500, 13px, 1, inherit);
    }

    &_label {
        @include fontStyle(Roboto, normal, 400, 13px, 18px, #373737);
        text-align: center;
        width: 100%;
        box-sizing: border-box;
        margin: 10px 0 0;
    }
}

::v-deep(.appeal_alert) {
    .ant-alert-icon {
        font-size: 18px;
        color: #FAAD14;
    }

    .ant-alert-message {
        @include fontStyle(Roboto, normal, 500, 15px, 22px, #AD6800);
    }

    .ant-alert-description {
        @include fontStyle(Roboto, normal, 400, 14px, 20px, #AD6800);
    }
}

.docs {
    &_header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 40px;
        margin-bottom: 20px;
    }

    &_title {
        @include fontStyle(Roboto, normal, 500, 20px, 28px, #373737);
        margin: 0;
    }

    &_toolbar {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 20px;
        margin-bottom: 16px;
    }

    &_filters {
        display: flex;
        flex-wrap: nowrap;
        overflow-x: auto;
        gap: 8px;
        padding-bottom: 4px;
    }

    &_filter {
        @include fontStyle(Roboto, normal, 400, 14px, 20px, #373737);
        background: none;
        border: none;
        border-radius: 6px;
        padding: 8px 16px;
        cursor: pointer;
        white-space: nowrap;
        flex: 0 0 auto;

        span {
            color: #909090;
        }

        &_active {
            background: #007BFF;
            color: #fff;

            span {
                color: #fff;
            }
        }
    }

}

// Имя docs_progress, а не progress: в проекте подключён bootstrap, у него
// свой .progress с серым фоном, height: 1rem и overflow: hidden.
.docs_progress {
    position: relative;
    display: flex;
    align-items: center;
    flex: 1;
    max-width: 220px;

    &_track {
        width: 100%;
        height: 10px;
        border-radius: 5px;
        background: #E8E8E8;
        overflow: hidden;
    }

    &_bar {
        height: 100%;
        border-radius: 5px;
        background: #007BFF;
    }

    // Бейдж сидит на конце заливки: центр по вертикали, середина пилюли — на границе.
    &_badge {
        @include fontStyle(Roboto, normal, 500, 13px, 24px, #fff);
        position: absolute;
        top: 50%;
        transform: translate(-50%, -50%);
        min-width: 42px;
        height: 24px;
        padding: 0 8px;
        border-radius: 12px;
        background: #007BFF;
        text-align: center;
    }
}

::v-deep(.docs_list) {
    .ant-list-item {
        background: #F4F4F8;
        border-radius: 8px;
        padding: 16px 20px;
        margin-bottom: 12px;
				@media (max-width: 767px) {
					flex-direction: column;
					align-items: flex-start;
					gap: 8px;
					width: 100%;
				}
    }

    .ant-list-item-meta-title {
        @include fontStyle(Roboto, normal, 500, 14px, 20px, #1A1A1A);
        margin: 0;
    }

    .ant-list-item-meta-description {
        @include fontStyle(Roboto, normal, 400, 13px, 18px, #909090);
    }

    .ant-list-item-meta-avatar {
        display: flex;
        align-items: center;
    }

		.ant-list-item-meta-content {
			width: 100% !important;
		}
}

.doc {
    &_icon {
        font-size: 18px;
        color: #909090;
    }

    &_upload {
        display: flex;
        align-items: center;
        height: 40px;
        background: #007BFF;
    }

    &_meta {
        display: flex;
        align-items: center;
        gap: 10px;
    }

    &_status {
        @include fontStyle(Roboto, normal, 500, 13px, 20px, #909090);
        padding: 1px 10px;
        border-radius: 10px;
        background: #F0F0F0;

        &_required {
            background: #FFF7E6;
            color: #AD6800;
        }

        &_pending_review {
            background: #E6F4FF;
            color: #0958D9;
        }

        &_provided {
            background: #F6FFED;
            color: #389E0D;
        }
    }

    &_date {
        @include fontStyle(Roboto, normal, 400, 13px, 18px, #909090);
    }

    &_attachments {
        @include fontStyle(Roboto, normal, 400, 13px, 18px, #909090);
        margin: 6px 0 0;
    }

    // Имена файлов КИАС — кнопка-ссылка, скачивание через download_url.
    &_attachment {
        margin-left: 6px;
        padding: 0;
        background: none;
        border: none;
        color: #007BFF;
        cursor: pointer;
        text-decoration: underline;
    }

    &_own_file {
        @include fontStyle(Roboto, normal, 400, 13px, 18px, #007BFF);
        display: inline-flex;
        align-items: center;
        gap: 6px;
        margin-top: 6px;
        padding: 0;
        background: none;
        border: none;
        cursor: pointer;
    }
}

.docs_empty {
    @include fontStyle(Roboto, normal, 400, 14px, 20px, #909090);
    margin: 0;
    padding: 20px 0;
    text-align: center;
}
</style>
